/**
 * LocalBest.ai First-Party Tracking Library
 * 
 * Manages visitor identity, session tracking, attribution capture,
 * and dataLayer events for GTM/pixel integration.
 */

const VISITOR_KEY = "ahp_visitor_id";
const SESSION_KEY = "ahp_session_id";
const SESSION_TS_KEY = "ahp_session_ts";
const FIRST_TOUCH_KEY = "ahp_first_touch";
const LAST_TOUCH_KEY = "ahp_last_touch";
const SESSION_TIMEOUT = 30 * 60 * 1000; // 30 minutes

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Visitor / Session Identity ──────────────────────────
function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export function getVisitorId() {
  let id = localStorage.getItem(VISITOR_KEY);
  if (!id) {
    id = generateId();
    localStorage.setItem(VISITOR_KEY, id);
  }
  return id;
}

export function getSessionId() {
  const now = Date.now();
  const lastTs = parseInt(localStorage.getItem(SESSION_TS_KEY) || "0", 10);
  let sid = localStorage.getItem(SESSION_KEY);

  if (!sid || now - lastTs > SESSION_TIMEOUT) {
    sid = generateId();
    localStorage.setItem(SESSION_KEY, sid);
  }
  localStorage.setItem(SESSION_TS_KEY, String(now));
  return sid;
}

// ─── Attribution Capture ──────────────────────────────────
const PARAM_KEYS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "fbclid", "gclid", "ttclid", "msclkid", "li_fat_id",
];

export function captureAttribution() {
  const params = new URLSearchParams(window.location.search);
  const attribution = {};
  let hasParams = false;

  for (const key of PARAM_KEYS) {
    const val = params.get(key);
    if (val) {
      attribution[key] = val;
      hasParams = true;
    }
  }

  attribution.referrer = document.referrer || "";
  attribution.landing_page = window.location.pathname + window.location.search;

  // Save first-touch (only if not already stored)
  if (hasParams) {
    const existing = getFirstTouch();
    if (!existing || !existing.utm_source) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({
        ...attribution,
        captured_at: new Date().toISOString(),
      }));
    }
    // Always update last-touch
    localStorage.setItem(LAST_TOUCH_KEY, JSON.stringify({
      ...attribution,
      captured_at: new Date().toISOString(),
    }));
  } else if (!getFirstTouch()) {
    // No UTM params but this is the first visit — store referrer-only first touch
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify({
      referrer: attribution.referrer,
      landing_page: attribution.landing_page,
      captured_at: new Date().toISOString(),
    }));
  }

  return attribution;
}

export function getFirstTouch() {
  try {
    return JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || "null");
  } catch { return null; }
}

export function getLastTouch() {
  try {
    return JSON.parse(localStorage.getItem(LAST_TOUCH_KEY) || "null");
  } catch { return null; }
}

export function getAttribution() {
  return {
    first_touch: getFirstTouch(),
    last_touch: getLastTouch(),
  };
}

// Get current URL attribution params (live from URL)
export function getCurrentUrlAttribution() {
  const params = new URLSearchParams(window.location.search);
  const result = {};
  for (const key of PARAM_KEYS) {
    const val = params.get(key);
    if (val) result[key] = val;
  }
  return result;
}

// ─── Event ID Generation (for deduplication) ─────────────
export function generateEventId() {
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

// ─── Browser Info ─────────────────────────────────────────
export function getBrowserInfo() {
  return {
    user_agent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    screen_width: window.screen?.width,
    screen_height: window.screen?.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight,
  };
}

// ─── DataLayer Push ───────────────────────────────────────
export function pushToDataLayer(eventName, data = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: eventName,
    event_id: data.event_id || generateEventId(),
    ...data,
    timestamp: new Date().toISOString(),
  });
}

// ─── Platform Event Mapping ───────────────────────────────
const EVENT_MAP = {
  listing_view: {
    meta: "ViewContent",
    ga4: "view_item",
    tiktok: "ViewContent",
    linkedin: "PageView",
    dataLayer: "directory_listing_view",
  },
  call_click: {
    meta: "Contact",
    ga4: "click_to_call",
    tiktok: "Contact",
    dataLayer: "directory_call_click",
  },
  booking_click: {
    meta: "Schedule",
    ga4: "book_appointment",
    tiktok: "Contact",
    dataLayer: "directory_booking_click",
  },
  form_submit: {
    meta: "Lead",
    ga4: "generate_lead",
    tiktok: "SubmitForm",
    dataLayer: "directory_form_submit",
  },
  website_click: {
    meta: "ViewContent",
    ga4: "click",
    tiktok: "ClickButton",
    dataLayer: "directory_website_click",
  },
  chat_click: {
    meta: "Contact",
    ga4: "contact",
    tiktok: "Contact",
    dataLayer: "directory_chat_start",
  },
  offer_click: {
    meta: "ViewContent",
    ga4: "select_promotion",
    tiktok: "ViewContent",
    dataLayer: "directory_offer_click",
  },
  directions_click: {
    meta: "FindLocation",
    ga4: "get_directions",
    tiktok: "ClickButton",
    dataLayer: "directory_directions_click",
  },
};

export function getMappedEventName(internalEvent, platform) {
  return EVENT_MAP[internalEvent]?.[platform] || internalEvent;
}

// ─── Send Event to Backend ────────────────────────────────
let eventQueue = [];
let flushTimer = null;

export function sendTrackingEvent(eventData) {
  eventQueue.push(eventData);
  
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushEventQueue, 2000);
  
  // Flush immediately if queue is large
  if (eventQueue.length >= 5) {
    flushEventQueue();
  }
}

async function flushEventQueue() {
  if (eventQueue.length === 0) return;
  
  const batch = [...eventQueue];
  eventQueue = [];
  flushTimer = null;

  try {
    if (batch.length === 1) {
      await fetch(`${API}/api/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batch[0]),
        keepalive: true,
      });
    } else {
      await fetch(`${API}/api/track/batch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ events: batch }),
        keepalive: true,
      });
    }
  } catch (err) {
    // Silently fail — tracking should never break the user experience
    console.warn("[Tracker] Failed to send events:", err.message);
  }
}

// Flush on page unload
if (typeof window !== "undefined") {
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      flushEventQueue();
    }
  });
}

// ─── Build Full Event Payload ─────────────────────────────
export function buildEventPayload(eventName, listingData = {}, consent = {}) {
  const urlAttribution = getCurrentUrlAttribution();
  const lastTouch = getLastTouch() || {};

  return {
    event_name: eventName,
    event_id: generateEventId(),
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    listing_id: listingData.id || listingData.listing_id || null,
    client_id: listingData.client_id || listingData.organization_id || null,
    company_name: listingData.name || listingData.company_name || null,
    category: listingData.category || listingData.category_name || null,
    subcategory: listingData.subcategory || null,
    city: listingData.city || null,
    state: listingData.state || null,
    market: listingData.market || null,
    page_url: window.location.href,
    referrer: document.referrer || null,
    utm_source: urlAttribution.utm_source || lastTouch.utm_source || null,
    utm_medium: urlAttribution.utm_medium || lastTouch.utm_medium || null,
    utm_campaign: urlAttribution.utm_campaign || lastTouch.utm_campaign || null,
    utm_content: urlAttribution.utm_content || lastTouch.utm_content || null,
    utm_term: urlAttribution.utm_term || lastTouch.utm_term || null,
    fbclid: urlAttribution.fbclid || lastTouch.fbclid || null,
    gclid: urlAttribution.gclid || lastTouch.gclid || null,
    ttclid: urlAttribution.ttclid || lastTouch.ttclid || null,
    msclkid: urlAttribution.msclkid || lastTouch.msclkid || null,
    li_fat_id: urlAttribution.li_fat_id || lastTouch.li_fat_id || null,
    consent_status: consent,
    browser_info: getBrowserInfo(),
  };
}
